import { useState } from 'react'
import type { CreationSlot } from '@rollcall/contracts'
import { useT } from './i18n-context.js'
import { contentName, optionLabel, slotTitle } from './labels.js'
import { slotDictKey } from '../slots.js'
import { TermHint } from './term-hint.js'

/**
 * Один слот плана создания.
 *
 * Компонент не знает, что за слот перед ним: имя, число выборов
 * и варианты пришли с сервера, а тот собрал их из записей контента
 * (D23). Поэтому новый слот — новый класс, вид, правило — появляется
 * здесь сам, без правки кода.
 *
 * Выбрать один — радиокнопки, несколько — флажки: это разные вопросы,
 * и подменять их одним списком значит врать о том, сколько можно взять.
 *
 * Замечания приходят готовыми строками, а не кодами: превращает код
 * в текст визард, в одном месте на всю форму. Иначе каждый компонент
 * знал бы про `CreationProblem`, а сообщения расползлись бы по трём
 * файлам.
 *
 * У длинного списка появляется поиск, и показывается он не целиком:
 * двести флажков подряд нельзя просмотреть глазами, сколько бы
 * их ни объявила запись контента, а на телефоне такой список
 * ещё и уводит кнопку «готово» на пять экранов вниз. Показывается
 * начало списка, остальное открывает поиск; выбранное видно всегда,
 * даже если поиск его не нашёл.
 */

/** Со скольких вариантов список нужно уметь искать. */
const SEARCHABLE_FROM = 20

/** Сколько вариантов длинного списка показывать сразу. */
const VISIBLE_LIMIT = 12

export function SlotField({
  slot,
  picked,
  problems,
  onChange,
}: {
  readonly slot: CreationSlot
  readonly picked: readonly string[]
  readonly problems: readonly string[]
  readonly onChange: (values: readonly string[]) => void
}) {
  const t = useT()
  const many = slot.choose > 1
  const errorId = `slot-error-${slot.key}`
  const invalid = problems.length > 0
  const [search, setSearch] = useState('')

  const searchable = slot.options.length >= SEARCHABLE_FROM
  const needle = search.trim().toLocaleLowerCase()
  // Выбранное из списка не пропадает: иначе, набрав следующее
  // название, человек перестал бы видеть, что уже взял.
  const shown = slot.options.filter(
    (option) =>
      needle === '' ||
      picked.includes(option.value) ||
      optionLabel(t, slot, option).toLocaleLowerCase().includes(needle),
  )

  // Выбранное показывается всегда, остальное — до предела: обрезать
  // список вместе с выбранным значило бы прятать от человека его же
  // ответ.
  const kept = new Set(picked)
  const rest = new Set(
    shown.filter((option) => !kept.has(option.value)).slice(0, VISIBLE_LIMIT),
  )
  const visible = searchable
    ? shown.filter((option) => kept.has(option.value) || rest.has(option))
    : shown
  const hidden = shown.length - visible.length

  const full = many && picked.length >= slot.choose

  const toggle = (value: string, checked: boolean) => {
    if (!many) {
      onChange([value])
      return
    }
    const next = picked.filter((item) => item !== value)
    if (checked) next.push(value)
    onChange(next)
  }

  return (
    <fieldset
      className="app-slot"
      data-slot={slot.key}
      data-invalid={invalid ? 'true' : undefined}
      aria-describedby={invalid ? errorId : undefined}
    >
      <legend className="app-slot-legend">{slotTitle(t, slot)}</legend>
      {/* Знак вопроса — строкой под заголовком, а не внутри него.
          Внутри `legend` он работал, но раскрытое описание вылезало
          за рамку поля: браузер рисует верхнюю границу `fieldset`
          по середине легенды, и высокая легенда её разрывает.
          «Разносторонность» — название, а что она значит, написано
          в самой записи (D57); у слотов, за которыми стоит строка
          таблицы класса, описания нет, и знака вопроса там
          не появляется. */}
      <div className="app-slot-head">
        <p className="app-slot-hint" data-full={full ? '' : undefined}>
          {t('wizard.choose', { count: slot.choose })} ·{' '}
          {t('wizard.chosen', { count: picked.length })}
        </p>
        <TermHint name={slotTitle(t, slot)} text={slot.text} textRu={slot.textRu} />
      </div>

      {searchable && (
        <label className="app-field">
          <span>{t('wizard.search')}</span>
          <input
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
            }}
          />
        </label>
      )}

      {/* Список прокручивается внутри себя, а не уносит страницу вниз:
          заговоров у волшебника три из девяти, а список класса —
          двести с лишним записей. */}
      <div className="app-slot-options app-scroll-list">
        {visible.map((option) => (
          // Знак вопроса — снаружи `label`: внутри нажатие на него
          // попало бы по самой радиокнопке, то есть открытие описания
          // делало бы выбор.
          <div key={option.value} className="app-option-row">
            <label className="app-option">
              <input
                type={many ? 'checkbox' : 'radio'}
                name={slot.key}
                value={option.value}
                checked={picked.includes(option.value)}
                // Больше положенного не выбрать: лишние варианты гаснут,
                // как гаснет вторая двойка в пуле характеристик. Отказ
                // сервера при сохранении — слишком поздно, чтобы быть
                // ответом на «сколько можно взять».
                // Владение уже есть (D63): вариант гаснет, но остаётся
                // на месте — список не прыгает от выборов в соседних
                // слотах. Уже отмеченный не гаснет: его надо снять.
                disabled={
                  (full || option.taken !== undefined) && !picked.includes(option.value)
                }
                onChange={(event) => {
                  toggle(option.value, event.target.checked)
                }}
              />
              {/* Вариант приходит с сервера английской подписью из
                  источника; на экране он называется по-русски, если
                  перевод есть, и оригиналом, если нет. */}
              <span>{optionLabel(t, slot, option)}</span>
              {option.taken !== undefined && (
                <span className="app-option-note" data-testid={`taken-${option.value}`}>
                  {option.taken.kind === 'granted'
                    ? t('wizard.taken.granted', {
                        source: contentName(t, option.taken.source),
                      })
                    : t('wizard.taken.picked', {
                        slot: t(`slot.${slotDictKey(option.taken.slot)}`, {
                          source: '',
                          defaultValue: option.taken.slot,
                        }),
                      })}
                </span>
              )}
            </label>
            <TermHint
              name={optionLabel(t, slot, option)}
              text={option.text}
              textRu={option.textRu}
              contentId={option.value}
            />
          </div>
        ))}
      </div>

      {hidden > 0 && (
        <p className="app-slot-hint">{t('wizard.moreOptions', { count: hidden })}</p>
      )}

      {invalid && (
        <ul className="app-problems" id={errorId} role="alert">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      )}
    </fieldset>
  )
}
